import {
    Alert,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    CardTitle, Feedback,
    FeedbackPopover,
    Field,
    Grid,
    Input,
    Label
} from "@opensoftpt/at-ui";
import {ChangeEvent, FormEvent, useEffect, useState} from "react";
import {validateCodigo, validateEmptyField, validateNif} from "../utils/validations";
import {useTranslation} from "react-i18next";

export interface ValidarPageProps {
    errorMessage: string | undefined;
    nif: string | undefined;
    codigoValidacao: string | undefined;
    language: string | undefined;
}

export function ValidarDocumento(props: Readonly<ValidarPageProps>) {

    const {i18n, t} = useTranslation("validarDocumento");

    useEffect(() => {
        i18n.changeLanguage(props.language || 'pt');
    }, [i18n, props.language]);

    const formAction = props.language === 'en' ? '/certcomp/en/validar/detalhe' : '/certcomp/validar/detalhe';

    const COD_SIZE = 12;
    const NIF_SIZE = 9;

    const [nif, setNif] = useState(props.nif || '');
    const [codigoValidacao, setCodigoValidacao] = useState(props.codigoValidacao || '');
    const [errorMessage, setErrorMessage] = useState(props.errorMessage || "");
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [invalidFieldMessage, setInvalidFieldMessage] = useState(false);
    const [nifError, setNifError] = useState(false);
    const [codError, setCodError] = useState(false);
    const [nifErrorMessage, setNifErrorMessage] = useState("");
    const [codErrorMessage, setCodErrorMessage] = useState("");
    const [nifPopoverOpen, setNifPopoverOpen] = useState(true);
    const [codigoPopoverOpen, setCodigoPopoverOpen] = useState(true);
    const [nifChangedAfterSubmit, setNifChangedAfterSubmit] = useState(false);
    const [codigoChangedAfterSubmit, setCodigoChangedAfterSubmit] = useState(false);

    const handleNifChange = (event: ChangeEvent<HTMLInputElement>) => {
        setNifError(false);
        setNifChangedAfterSubmit(true);
        if(event.target.value.length > NIF_SIZE) {
            return;
        }
        const processedValue = event.target.value.replace(/\D/g, '');
        setNif(processedValue);
    };

    const handleCodigoChange = (event: ChangeEvent<HTMLInputElement>) => {
        setCodError(false);
        setCodigoChangedAfterSubmit(true);
        if(event.target.value.length > COD_SIZE) {
            return;
        }
        const processedValue = event.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        setCodigoValidacao(processedValue);
    };

    const validateField = (fieldName: 'nif' | 'codigo'): boolean => {
        const value = fieldName === 'nif' ? nif : codigoValidacao;
        const validator = fieldName === 'nif' ? validateNif : validateCodigo;
        const setError = fieldName === 'nif' ? setNifError : setCodError;
        const setErrorMessage = fieldName === 'nif' ? setNifErrorMessage : setCodErrorMessage;
        const setPopoverOpen = fieldName === 'nif' ? setNifPopoverOpen : setCodigoPopoverOpen;
        const invalidMessage = fieldName === 'nif' ? t("errors.nifInvalido") : t("errors.codigoInvalido");

        if (validateEmptyField(value)) {
            setError(true);
            setErrorMessage(t("errors.requiredField"));
            setPopoverOpen(true);
            return true;
        }

        if (!validator(value)) {
            setError(true);
            setErrorMessage(invalidMessage);
            setPopoverOpen(true);
            return true;
        }

        return false;
    };

    const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setErrorMessage("");
        setInvalidFieldMessage(false);
        setNifChangedAfterSubmit(false);
        setCodigoChangedAfterSubmit(false);
        setNifError(false);
        setCodError(false);
        setNifPopoverOpen(false);
        setCodigoPopoverOpen(false);

        const hasNifError = validateField('nif');
        const hasCodigoError = validateField('codigo');

        if (hasNifError || hasCodigoError) {
            setGenericErrorMessage();
            return;
        }

        setIsButtonDisabled(true);
        setTimeout(() => {
            setIsButtonDisabled(false);
        }, 3000);

        event.currentTarget.submit();
    };

    const checkNifPopoverState = (newState : boolean) => {
        if(nifChangedAfterSubmit) {
            setNifPopoverOpen(newState);
        }
    }

    const checkCodigoPopoverState = (newState : boolean) => {
        if(codigoChangedAfterSubmit) {
            setCodigoPopoverOpen(newState);
        }
    }

    const setGenericErrorMessage= (): void => {
        setInvalidFieldMessage(true);
        setErrorMessage(t("errors.invalidField"));
    }

    return (
        <>
            <h1>{t("title")}</h1>
            <Grid container>
                <Grid xs={12}>
                    <legend>
                        <small>
                            {t("textoDetalhe")}
                        </small>
                    </legend>
                    {errorMessage.length > 0 &&
                        <Alert id={"detalhe-error-alert"} variant="subtle" severity={invalidFieldMessage ? "danger" : "warning"} showCloseButton icon={null}
                               onOpenChange={() => setErrorMessage("")}>
                            {errorMessage}
                        </Alert>
                    }
                    <Card asChild
                          color="primary">
                        <form method="POST" action={formAction} onSubmit={handleSubmit}>
                            <input type="hidden" name="_csrf" value={document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')!} />
                            <CardHeader>
                                <CardTitle>
                                    {t("cardTitle")}
                                </CardTitle>
                            </CardHeader>
                            <CardBody>
                                <Grid container>
                                    <Grid sm={6} xs={12}>
                                        <Field>
                                            <Label htmlFor="nif-input">
                                                {t("nifLabel")}
                                            </Label>
                                            <Input
                                                id="nif-input"
                                                name="nif"
                                                inputMode="numeric"
                                                value={nif}
                                                status={nifError ? "invalid" : undefined}
                                                onFocus={() => {setNifChangedAfterSubmit(true)}}
                                                endAdornment={nifError &&
                                                    <FeedbackPopover
                                                        open={nifPopoverOpen} defaultOpen
                                                        onOpenChange={checkNifPopoverState}
                                                        buttonProps={{
                                                            "aria-label": t("popoverLabel"),
                                                            onClick: () => {
                                                                setNifPopoverOpen(!nifPopoverOpen);
                                                                setNifChangedAfterSubmit(true);
                                                            }
                                                        }}>
                                                        <Feedback type="error">{nifErrorMessage}</Feedback>
                                                    </FeedbackPopover>
                                                }
                                                onChange={handleNifChange}
                                            />
                                        </Field>
                                    </Grid>
                                    <Grid sm={6} xs={12}>
                                        <Field>
                                            <Label htmlFor="codigo-input">
                                                {t("codigoLabel")}
                                            </Label>
                                            <Input
                                                id="codigo-input"
                                                name="codigoValidacao"
                                                value={codigoValidacao}
                                                status={codError ? "invalid" : undefined}
                                                onFocus={() => {setCodigoChangedAfterSubmit(true)}}
                                                endAdornment={codError &&
                                                    <FeedbackPopover
                                                        open={codigoPopoverOpen} defaultOpen
                                                        onOpenChange={checkCodigoPopoverState}
                                                        buttonProps={{
                                                            "aria-label": t("popoverLabel"),
                                                            onClick: () => {
                                                                setCodigoPopoverOpen(!codigoPopoverOpen);
                                                                setCodigoChangedAfterSubmit(true);
                                                            }
                                                        }}>
                                                        <Feedback type="error">{codErrorMessage}</Feedback>
                                                    </FeedbackPopover>
                                                }
                                                onChange={handleCodigoChange}
                                            />
                                        </Field>
                                    </Grid>
                                </Grid>
                            </CardBody>
                            <CardFooter>
                                <button type="submit"
                                        disabled={isButtonDisabled}
                                        className="btn btn-primary btn-sm">
                                    {t("continuar")}
                                </button>
                            </CardFooter>
                        </form>
                    </Card>
                </Grid>
            </Grid>
        </>
    )
}